<template>
  <config-base showPlanDetails ref="baseRef" v-bind="$props">
    <template #topAlert>
      <zm-banner show-close v-if="hasOneTimeWebinar" type="warning" show-icon class="opc-base-alert">
        <p class="opc-base-alert__message" v-html="notification"  @mouseover="() => $tracking.buyInteraction('banner - product level', trackingPlanKey + ' - ' + notification + ' - impression')" />
      </zm-banner>
    </template>
    <template #config>
      <capacity-config-list showMonthlyPriceFromCycle :config-plan-type="planType" :config-capacity-key="capacityKey" :configRowName="configRowName" capacityUnitI18nKey='billing.attendees'></capacity-config-list>
    </template>
    <template #footer v-if="canRmv">
      <zm-button data-testid="checkout-button-90" type="link" ghost @click="removePlanByKey" :aria-label="'remove ' + trackingPlanKey + ' plan'">{{ $t('billing.common.remove') }}</zm-button>
    </template>
  </config-base>
</template>

<script>
import planCommon from '@/plans/WEBINAR/mixin';
import {getPlanTotalLicense} from "@/common/service/validate";
import plansMixin from "@/plans/mx/plan";
import ConfigList from "@/components/capacity/config-list.vue";
import ConfigBase from "@/plans/WEBINAR/config/base.vue";
import { computedCapacityWebinarPlans } from "@/common/service/computers/plans/webinar";
import PlanDetailsMixin from '@/plans/EVENT_SAAS/component/dialog/plan-details/mx-plan-details';
import {
  BILLING_CYCLE,
  planTypes,
} from '@/common/constants';
import { isBizPlusContext } from '@/plans/ZOOM_MEETING/common/utils';
import { mapGetters, mapMutations } from 'vuex';
import autoAddMixin from '@/common/mixin/autoAdd';
import MixedPlans from '@/plans/WEBINAR/mixins/mixed-plans';
import _ from 'lodash';
import TwoWayControl from '@/plans/WEBINAR/mixins/two-way-control';
import { isFromShoppingCart, isProductCanRemove } from '@/views/config/shopping-cart/persist/plan-remove';

const CapacityConfigList = {
  name: 'CapacityConfigList',
  extends: ConfigList,
  mixins: [planCommon, PlanDetailsMixin, autoAddMixin, MixedPlans, TwoWayControl],
  data() {
    return {
      addAnotherCapacityTrackingCta: 'add another webinar plan',
      handleAutoAddMap: {},
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'maxCycleValueBasePlan', 'basePlanTotalLicenses']),
    addAnotherCapacityCta() {
      return this.$t('billing.addAnother.webinar');
    },
    mx_annualPlanDisabled() {
      const isSingleUsePro = this.maxCycleValueBasePlan?.billing_cycle === BILLING_CYCLE.ONE_TIME;
      if (isSingleUsePro) return {};
      const currentBasePlanCycle = this.maxCycleValueBasePlan?.cycleValue || 0;
      const isAnnualDisabled = currentBasePlanCycle < 12;
      return {
        disabled: isAnnualDisabled,
        reason: isAnnualDisabled ? this.mx_disableAnnualTooltip : '',
      };
    },
  },
  methods: {
    ...mapMutations(['removePlan', 'updateSelectPlan', 'batchRemovePlan']),
    mx_getMaxHosts(capacity) {
      if (this.mx_isCapacityBelongToOneTime(capacity)) return 999;
      if (isBizPlusContext) {
        return getPlanTotalLicense(this.getSelectedPlans.filter(item => [planTypes.ZOOM_MEETING, planTypes.ZOOM_ONE_BUNDLE].includes(item.type))) || 999;
      }
      if (this.mx_canAutoAdd) {
        return this.mx_maxAutoAddLicense;
      }

      return this.basePlanTotalLicenses || 999;
    },
    mx_getIncreaseTips(capacity, license) {
      if (this.mx_isCapacityBelongToOneTime(capacity)) return '';
      const currentCapacityPlanCount = getPlanTotalLicense(computedCapacityWebinarPlans(this.selectedPlanList || [], capacity)) || license;
      return currentCapacityPlanCount >= this.mx_getMaxHosts() ? this.totalLimitDesc : '';
    },
    handleLicenseChange(newLicense, oldLicense, planInfo) {
      if (this.mx_canAutoAdd && planInfo.billingCycle !== BILLING_CYCLE.ONE_TIME) {
        this.handleAutoAdd(newLicense, oldLicense, planInfo);
      }
    },
    /**
     * plan license change sync base plan license
     * @param {number} newLicense newLicense
     * @param {number} oldLicense oldLicense
     * @param {object} planInfo
     * @param {number} planInfo.capacity
     * @param {string} planInfo.billingCycle
     */
    handleAutoAdd(newLicense, oldLicense, { capacity }) {
      if (!this.handleAutoAddMap[capacity]) {
        const self = this;
        this.handleAutoAddMap[capacity] =  _.debounce((newLicense, oldLicense, { capacity }) => {
          // cancel switch plan, reset license
          self.mx_handlePlanLicenseChange(newLicense).catch((previousLicense) => {
            const configRowInstance = _.get(self.$refs, [`configRow${capacity}`, '0']);
            if (configRowInstance) {
              configRowInstance.changeLicense(previousLicense);
            }
          });
        }, 300);
      }
      this.handleAutoAddMap[capacity](newLicense, oldLicense, { capacity });
    }
  },
}
export default {
  mixins: [plansMixin, planCommon],
  components: { ConfigBase, CapacityConfigList},
  computed:{
    ...mapGetters(['getSelectedPlans']),
    canRmv() {
      if(isFromShoppingCart() && !this.isContainWebinarSingleUse) {
        return isProductCanRemove(this.planList[0])
      }
      return this.$context.type !== planTypes.WEBINAR
    },
    hasOneTimeWebinar() {
      return this.getSelectedPlans.find(p => p.type === planTypes.WEBINAR && p.billing_cycle === BILLING_CYCLE.ONE_TIME);
    },
    notification() {
      return this.$t('billing.single_use_webinar.notification')
    },
    configRowName() {
      return 'CapacityConfigRow';
    },
    isContainWebinarSingleUse () {
      return this.getSelectedPlans.some(plan => plan?.plan_code === "zo_pro_single_use")
    }
  },
  mounted() {
    const hasWebinarPlan = this.getSelectedPlans.find(p => p.type === planTypes.WEBINAR);
    if (!hasWebinarPlan) {
      const firstMonthlyPlan = this.planList.sort((a,b) => a.capacity - b.capacity).find(p => p.billing_cycle === BILLING_CYCLE.MONTHLY);
      this.updateSelectPlan({
        plan_code: firstMonthlyPlan?.plan_code,
        cartInfo: {
          selected_number: 1
        }
      });
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
  }
};
</script>

<style lang="scss">
</style>
